import { GetTogglesRequest } from '@eggplant/api-requests'
import type { TogglesResponse } from '@eggplant/types'
import type { ReactNode } from 'react'
import {
  createContext,
  useState
} from 'react'

interface CustomScriptProps {
  isCustomScriptEnabled?: boolean
}

export const CustomScriptContext = createContext<CustomScriptProps | undefined>(undefined)

interface ProviderProps {
  children: ReactNode
  isCustomScriptEnabled?: boolean
}

export const CustomScriptContextProvider = ({
  children,
  isCustomScriptEnabled: isCustomScriptEnabledProp
}: ProviderProps) => {
  const [isCustomScriptEnabled, setIsCustomScriptEnabled] = useState<boolean>(!!isCustomScriptEnabledProp)

  const handleSuccess = ({ runtime_flags }: TogglesResponse) => {
    if (!runtime_flags) return

    const { ENABLE_CUSTOM_SCRIPTS } = runtime_flags

    setIsCustomScriptEnabled(ENABLE_CUSTOM_SCRIPTS)
  }

  const handleError = () => {
    setIsCustomScriptEnabled(false)
  }

  const value = { isCustomScriptEnabled }

  return (
    <GetTogglesRequest
      onError={handleError}
      onSuccess={handleSuccess}
    >
      {
        () =>
          <CustomScriptContext.Provider value={value}>
            {children}
          </CustomScriptContext.Provider>
      }
    </GetTogglesRequest>
  )
}

interface ConsumerProps {
  children: (context: CustomScriptProps) => React.ReactNode
}

export const CustomScriptContextConsumer = ({
  children: renderChildren
}: ConsumerProps) =>
  <CustomScriptContext.Consumer>
    {
      (context?: CustomScriptProps) => {
        if (context === undefined) {
          throw new Error('<CustomScriptContextConsumer /> must be used within <CustomScriptContextProvider />')
        }

        return renderChildren(context)
      }
    }
  </CustomScriptContext.Consumer>
